import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
    Body1,
    Flex,
    Heading2,
    IconCalendarToday,
    IconMoreHoriz,
    IconPerson,
    Stack,
    Subtitle2,
} from '@myphx-engineering/phx-design-system';

import { useMedicationConfigOverride } from '../../../../contexts/DrugProgramProvider';
import { getFragment } from '../../../../graphql/client/helpers/get-fragment';
import type { DigitallyReceivedPrescriberOrderInfoFragment } from '../../../../graphql/generated/graphql';
import { formatToLocalizedDate } from '../../../../util';
import { formatMedicationQuantity } from '../../../../util/formatting/prescriber-order';
import { getProductDescription } from '../../../../util/formatting/prescriber-order/get-product-description';
import { getAbsoluteRoute } from '../../../../util/get-absolute-route';

export type OrderPrescriptionDetailsProps = {
    prescriberOrder: DigitallyReceivedPrescriberOrderInfoFragment;
};

export const OrderPrescriptionDetails: FC<OrderPrescriptionDetailsProps> = ({
    prescriberOrder,
}) => {
    const { t, i18n } = useTranslation('lilly');
    const navigate = useNavigate();

    const configOverride = useMedicationConfigOverride();

    const medication = getFragment(prescriberOrder.authorizedMedication);
    const patient = getFragment(prescriberOrder.patient);

    const ndcKey =
        medication.__typename === 'UnknownMedication' && medication.originalNdc
            ? medication.originalNdc
            : medication.id;
    const medicationOverride = configOverride.ndcInfo[ndcKey];
    const quantity = formatMedicationQuantity(
        prescriberOrder.quantity,
        medication,
        medicationOverride
    );

    const productDescription = getProductDescription(
        prescriberOrder,
        i18n,
        medicationOverride
    );

    const { assignmentTimestamp } = prescriberOrder;
    const sentToPharmacyDate = assignmentTimestamp
        ? formatToLocalizedDate(
              new Date(assignmentTimestamp),
              { dateStyle: 'medium' },
              i18n.language
          )
        : null;

    return (
        <Stack gap="sm">
            <Heading2>
                {t('orderConfirmation.prescriptionDetails.header')}
            </Heading2>
            <Subtitle2>{productDescription}</Subtitle2>
            <Stack gap="xxs">
                <Body1>
                    {t('orderConfirmation.prescriptionDetails.quantity', {
                        quantity,
                    })}
                </Body1>
                <Flex gap="xs">
                    <IconPerson color="primary" />
                    <Body1>
                        {t(
                            'orderConfirmation.prescriptionDetails.patientInfo',
                            {
                                firstName: patient?.firstName,
                                lastName: patient?.lastName,
                            }
                        )}
                    </Body1>
                </Flex>
                {sentToPharmacyDate && (
                    <Flex gap="xs">
                        <IconCalendarToday color="primary" />
                        <Body1>
                            {t(
                                'orderConfirmation.prescriptionDetails.sentToPharmacyDate',
                                {
                                    // TODO: get date from MedicationRequest
                                    date: sentToPharmacyDate,
                                }
                            )}
                        </Body1>
                    </Flex>
                )}
                <Flex
                    gap="xs"
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                        navigate(
                            getAbsoluteRoute(
                                'order.prescriberOrderId.summary',
                                {
                                    prescriberOrderId:
                                        prescriberOrder.drxId ?? '',
                                }
                            )
                        );
                    }}
                >
                    <IconMoreHoriz color="primary" />
                    <Body1>
                        {t(
                            'orderConfirmation.prescriptionDetails.viewMoreDetails'
                        )}
                    </Body1>
                </Flex>
            </Stack>
        </Stack>
    );
};
